// ============================================================
//  src/admin/pages/AdminLogsPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { listLogs } from '../api/adminClient';
import { PageHeader, Pagination } from '../components/UI';

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

const LEVEL_COLOR = {
  info:  'var(--bf-text-muted)',
  warn:  'var(--bf-warning)',
  error: 'var(--bf-danger)',
};

export default function AdminLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [level, setLevel] = useState('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    listLogs({ page, limit, level, action })
      .then(res => { setLogs(res.data.logs); setTotal(res.data.total); })
      .catch(err => toast.error(err?.response?.data?.message || 'Could not load logs'))
      .finally(() => setLoading(false));
  }, [page, level, action]);

  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="bf-page">
      <PageHeader title="System Logs" subtitle={`${total.toLocaleString('en-NG')} log entries`} />

      <div className="bf-toolbar">
        <input
          className="bf-input"
          placeholder="Filter by action (e.g. user.block, bot.suspend)…"
          value={action}
          onChange={e => { setAction(e.target.value); setPage(1); }}
        />
        <select className="bf-select" value={level} onChange={e => { setLevel(e.target.value); setPage(1); }}>
          <option value="">All levels</option>
          <option value="info">Info</option>
          <option value="warn">Warning</option>
          <option value="error">Error</option>
        </select>
      </div>

      <div className="bf-card">
        <div className="bf-card-body-flush">
          {loading ? (
            <div className="bf-empty"><span className="spinner spinner-dark" /> Loading logs…</div>
          ) : logs.length === 0 ? (
            <div className="bf-empty">No logs found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {logs.map(l => (
                <div key={l.glId} style={{ padding: '11px 18px', borderBottom: '1px solid var(--bf-border)', fontSize: 12.5 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10.5, color: LEVEL_COLOR[l.glLevel] }}>{l.glLevel}</span>
                    <code className="bf-mono" style={{ fontSize: 12 }}>{l.glAction}</code>
                    {l.glActor && <span className="bf-muted">by {l.glActor}</span>}
                    {l.glTarget && <span className="bf-muted">→ {l.glTarget}</span>}
                    <span className="bf-muted" style={{ marginLeft: 'auto' }}>{fmtDate(l.glCreatedAt)}</span>
                  </div>
                  {l.glMessage && <div style={{ marginTop: 4, color: 'var(--bf-text-2)' }}>{l.glMessage}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} limit={limit} />
    </div>
  );
}